/**
 * Debug panel that lists every corrective shape key defined in the bindings
 * JSON, shows live activation weights, and lets the user click a corrective
 * to apply its recorded pose so the deformation can be visually verified.
 *
 * Only visible when debug mode is active and the user is in pose mode.
 * Toggled from the Debug menu like every other debug panel.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../app/store.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { getRestPoses } from "../mannequin/boneRegistry.ts";
import {
  type CorrectiveBinding,
  type CorrectiveDefinition,
  getCorrectiveDefinitions,
} from "../mannequin/correctiveShapeKeys.ts";
import {
  mirrorQuaternion,
  type Quat4,
  qmul,
} from "../mannequin/quaternionMath.ts";
import { DraggablePanel } from "../ui/DraggablePanel.tsx";
import { usePanelSelection } from "../ui/floatingPanelStore.ts";
import { COLOR_TEXT_DIM, COLOR_TEXT_FAINT, Z_DEBUG } from "../ui/tokens.ts";

// ---------------------------------------------------------------------------
// Per-frame debug data (written by MannequinRenderer)
// ---------------------------------------------------------------------------

export interface CorrectiveDebugSnapshot {
  bindings: readonly CorrectiveBinding[];
  weights: Map<number, number>;
}

// ---------------------------------------------------------------------------
// Flagged correctives (localStorage)
// ---------------------------------------------------------------------------

const FLAGGED_KEY = "poseref-debug-flagged-correctives";

function loadFlagged(): Set<string> {
  try {
    const raw = localStorage.getItem(FLAGGED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveFlagged(set: Set<string>): void {
  try {
    localStorage.setItem(FLAGGED_KEY, JSON.stringify([...set]));
  } catch {
    /* quota exceeded */
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Unique key for a definition (joint + action). */
function defKey(d: CorrectiveDefinition): string {
  return `${d.joint}:${d.action}`;
}

/** Group definitions by joint, sorted alphabetically. */
function groupByJoint(
  defs: CorrectiveDefinition[],
): Array<{ joint: string; actions: CorrectiveDefinition[] }> {
  const map = new Map<string, CorrectiveDefinition[]>();
  for (const d of defs) {
    let arr = map.get(d.joint);
    if (!arr) {
      arr = [];
      map.set(d.joint, arr);
    }
    arr.push(d);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([joint, actions]) => ({
      joint,
      actions: actions.sort((a, b) => a.action.localeCompare(b.action)),
    }));
}

/** Degrees string from radians. */
function degStr(rad: number): string {
  return `${((rad * 180) / Math.PI).toFixed(0)}°`;
}

// ---------------------------------------------------------------------------
// Weight bar color
// ---------------------------------------------------------------------------

function weightColor(w: number): string {
  if (w >= 0.95) return "#0f0";
  if (w >= 0.5) return "#ff0";
  if (w > 0.01) return "#f80";
  return COLOR_TEXT_FAINT;
}

// ---------------------------------------------------------------------------
// Side suffix helpers (mirrors makeBoneName in correctiveShapeKeys.ts)
// ---------------------------------------------------------------------------

function makeBoneName(base: string, side: string): string {
  const dotIdx = base.lastIndexOf(".");
  if (dotIdx >= 0) return base.slice(0, dotIdx) + side + base.slice(dotIdx + 1);
  return base + side;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CorrectiveShapeKeysDebugPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.correctiveShapeKeys);
  const poseMode = useStore((s) => s.poseMode);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const selectedObjects = useSelectedLeafObjectIds();
  const setBonePoses = useStore((s) => s.setBonePoses);
  const sel = usePanelSelection("debug-corrective-shape-keys", () =>
    togglePanel("correctiveShapeKeys"),
  );

  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [isolated, setIsolated] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [flagged, setFlagged] = useState<Set<string>>(loadFlagged);
  const weightsRef = useRef<HTMLDivElement>(null);

  // Sync isolate state to the global read by MannequinRenderer
  useEffect(() => {
    if (!isolated) {
      window.__poseref_correctiveIsolate = null;
      return;
    }
    const snap = window.__poseref_correctiveDebug;
    if (!snap) {
      window.__poseref_correctiveIsolate = null;
      return;
    }
    const indices = new Set<number>();
    for (const b of snap.bindings) {
      const parts = b.groupKey.split(":");
      if (parts[0] === "combo") continue;
      if (`${parts[0]}:${parts[2]}` === isolated) indices.add(b.morphIndex);
    }
    window.__poseref_correctiveIsolate = indices.size > 0 ? indices : null;
    return () => {
      window.__poseref_correctiveIsolate = null;
    };
  }, [isolated]);

  const toggleIsolate = useCallback((key: string) => {
    setIsolated((prev) => (prev === key ? null : key));
  }, []);

  const toggleFlagged = useCallback((key: string) => {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveFlagged(next);
      return next;
    });
  }, []);

  // All definitions from the bindings JSON (static after load)
  const allDefs = useMemo(() => getCorrectiveDefinitions(), []);
  const groups = useMemo(() => groupByJoint(allDefs), [allDefs]);

  // Filtered groups
  const filtered = useMemo(() => {
    if (!filter) return groups;
    const lc = filter.toLowerCase();
    return groups
      .map((g) => ({
        joint: g.joint,
        actions: g.actions.filter(
          (d) =>
            d.joint.includes(lc) ||
            d.action.includes(lc) ||
            d.bone.includes(lc),
        ),
      }))
      .filter((g) => g.actions.length > 0);
  }, [groups, filter]);

  // ---------------------------------------------------------------------------
  // Per-frame weight polling (RAF loop — same pattern as BoneDebugPanel)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!debugEnabled || !panelOpen || !poseMode) return;

    let raf: number;
    let boundDefsWritten = false;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const el = weightsRef.current;
      if (!el) return;

      const snap = window.__poseref_correctiveDebug;
      if (!snap) return;

      // One-time: mark which definitions have morph targets in the GLB
      if (!boundDefsWritten) {
        boundDefsWritten = true;
        const boundDefs = new Set<string>();
        for (const b of snap.bindings) {
          const parts = b.groupKey.split(":");
          if (parts[0] === "combo") continue;
          boundDefs.add(`${parts[0]}:${parts[2]}`);
        }
        const badges = el.querySelectorAll<HTMLSpanElement>("[data-bound-key]");
        for (const badge of badges) {
          const dk = badge.dataset.boundKey!;
          badge.textContent = boundDefs.has(dk) ? "" : "no morph";
        }
      }

      // Build a lookup: groupKey → max weight across keyframes in that group
      const groupWeights = new Map<string, number>();
      for (const b of snap.bindings) {
        const w = snap.weights.get(b.morphIndex) ?? 0;
        const prev = groupWeights.get(b.groupKey) ?? 0;
        if (w > prev) groupWeights.set(b.groupKey, w);
      }

      // Map definition keys to groupKeys via side expansion
      // Each definition (joint:action) has up to two sides (l, r)
      const defWeights = new Map<string, number>();
      for (const b of snap.bindings) {
        // Extract joint:action from groupKey (format: "joint:side:action" or "combo:...")
        const parts = b.groupKey.split(":");
        if (parts[0] === "combo") continue;
        const dk = `${parts[0]}:${parts[2]}`;
        const w = snap.weights.get(b.morphIndex) ?? 0;
        const prev = defWeights.get(dk) ?? 0;
        if (w > prev) defWeights.set(dk, w);
      }

      // Update all weight cells in the DOM
      const cells = el.querySelectorAll<HTMLSpanElement>("[data-def-key]");
      for (const cell of cells) {
        const dk = cell.dataset.defKey!;
        const w = defWeights.get(dk) ?? 0;
        const pct = (w * 100).toFixed(0);
        cell.textContent = w > 0.001 ? `${pct}%` : "";
        cell.style.color = weightColor(w);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [debugEnabled, panelOpen, poseMode]);

  // ---------------------------------------------------------------------------
  // Apply recorded pose
  // ---------------------------------------------------------------------------

  const applyPose = useCallback(
    (def: CorrectiveDefinition) => {
      const objectId = [...selectedObjects][0];
      if (!objectId) return;
      const restPoses = getRestPoses(objectId);
      if (!restPoses) return;

      const poses = new Map<string, Quat4>();

      if (def.unsided) {
        // Center bone — apply target directly
        const rest = restPoses.get(def.bone);
        if (rest) {
          poses.set(def.bone, qmul(rest, def.targetRotation));
        }
      } else {
        // Sided bone — apply to both L and R
        for (const [suffix, sideChar] of [
          ["L", "l"],
          ["R", "r"],
        ] as const) {
          const boneName = makeBoneName(def.bone, suffix);
          const rest = restPoses.get(boneName);
          if (!rest) continue;
          const target: Quat4 =
            sideChar === "r"
              ? mirrorQuaternion(def.targetRotation)
              : def.targetRotation;
          poses.set(boneName, qmul(rest, target));
        }
      }

      if (poses.size > 0) setBonePoses(objectId, poses);
    },
    [selectedObjects, setBonePoses],
  );

  const resetPoseBones = useCallback(
    (def: CorrectiveDefinition) => {
      const objectId = [...selectedObjects][0];
      if (!objectId) return;
      const restPoses = getRestPoses(objectId);
      if (!restPoses) return;

      const poses = new Map<string, Quat4>();
      if (def.unsided) {
        const rest = restPoses.get(def.bone);
        if (rest) poses.set(def.bone, rest);
      } else {
        for (const suffix of ["L", "R"] as const) {
          const boneName = makeBoneName(def.bone, suffix);
          const rest = restPoses.get(boneName);
          if (rest) poses.set(boneName, rest);
        }
      }
      if (poses.size > 0) setBonePoses(objectId, poses);
    },
    [selectedObjects, setBonePoses],
  );

  const handleSelect = useCallback(
    (def: CorrectiveDefinition) => {
      const key = defKey(def);
      if (selected === key) {
        resetPoseBones(def);
        setSelected(null);
      } else {
        setSelected(key);
        applyPose(def);
      }
    },
    [selected, applyPose, resetPoseBones],
  );

  const toggleCollapse = useCallback((joint: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(joint)) next.delete(joint);
      else next.add(joint);
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!debugEnabled || !panelOpen || !poseMode) return null;

  return (
    <DraggablePanel
      id="debug-corrective-shape-keys"
      defaultPosition={{ top: 8, right: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#ff0"
      {...sel}
      style={{ width: 280 }}
      header={
        <span
          style={{
            color: "#ff0",
            fontWeight: "bold",
            fontFamily: "monospace",
            fontSize: 10,
          }}
        >
          CORRECTIVE SHAPE KEYS
        </span>
      }
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          fontFamily: "monospace",
          fontSize: 10,
          borderRadius: "0 0 6px 6px",
          pointerEvents: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Search */}
        <div style={{ padding: "6px 8px 4px" }}>
          <input
            type="text"
            placeholder="filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-no-drag
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,0,0.2)",
              borderRadius: 3,
              color: "#ff0",
              fontFamily: "monospace",
              fontSize: 10,
              padding: "3px 6px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Scrollable list */}
        <div
          ref={weightsRef}
          style={{
            maxHeight: 400,
            overflowY: "auto",
            padding: "0 8px 6px",
          }}
        >
          {filtered.map((group) => (
            <JointGroup
              key={group.joint}
              joint={group.joint}
              actions={group.actions}
              collapsed={collapsed.has(group.joint)}
              selected={selected}
              isolated={isolated}
              flagged={flagged}
              onToggleCollapse={toggleCollapse}
              onSelect={handleSelect}
              onToggleIsolate={toggleIsolate}
              onToggleFlag={toggleFlagged}
            />
          ))}
          {filtered.length === 0 && (
            <div style={{ color: COLOR_TEXT_FAINT, padding: "8px 0" }}>
              no matches
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            color: COLOR_TEXT_DIM,
            borderTop: "1px solid #333",
            padding: "4px 8px",
            fontSize: 9,
          }}
        >
          {allDefs.length} definitions
          {isolated && <span style={{ color: "#0ff" }}> — isolated</span>}
          {flagged.size > 0 && (
            <span style={{ color: "#e88" }}> — {flagged.size} flagged</span>
          )}
        </div>
      </div>
    </DraggablePanel>
  );
}

// ---------------------------------------------------------------------------
// Joint group (collapsible section)
// ---------------------------------------------------------------------------

function JointGroup({
  joint,
  actions,
  collapsed,
  selected,
  isolated,
  flagged,
  onToggleCollapse,
  onSelect,
  onToggleIsolate,
  onToggleFlag,
}: {
  joint: string;
  actions: CorrectiveDefinition[];
  collapsed: boolean;
  selected: string | null;
  isolated: string | null;
  flagged: Set<string>;
  onToggleCollapse: (joint: string) => void;
  onSelect: (def: CorrectiveDefinition) => void;
  onToggleIsolate: (key: string) => void;
  onToggleFlag: (key: string) => void;
}) {
  const flagCount = actions.filter((d) => flagged.has(defKey(d))).length;
  return (
    <div style={{ marginTop: 4 }}>
      <button
        data-no-drag
        onClick={() => onToggleCollapse(joint)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          width: "100%",
          padding: "2px 0",
          border: "none",
          background: "transparent",
          color: "#ff0",
          fontFamily: "monospace",
          fontSize: 10,
          fontWeight: "bold",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ width: 10, flexShrink: 0, fontSize: 8 }}>
          {collapsed ? "\u25B6" : "\u25BC"}
        </span>
        {joint}
        {flagCount > 0 && (
          <span style={{ color: "#e88", fontWeight: "normal", fontSize: 9 }}>
            {flagCount}
          </span>
        )}
        <span
          style={{
            color: COLOR_TEXT_FAINT,
            fontWeight: "normal",
            marginLeft: "auto",
          }}
        >
          {actions.length}
        </span>
      </button>
      {!collapsed &&
        actions.map((def) => (
          <ActionRow
            key={def.action}
            def={def}
            isSelected={selected === defKey(def)}
            isIsolated={isolated === defKey(def)}
            isFlagged={flagged.has(defKey(def))}
            onSelect={onSelect}
            onToggleIsolate={onToggleIsolate}
            onToggleFlag={onToggleFlag}
          />
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action row
// ---------------------------------------------------------------------------

function ActionRow({
  def,
  isSelected,
  isIsolated,
  isFlagged,
  onSelect,
  onToggleIsolate,
  onToggleFlag,
}: {
  def: CorrectiveDefinition;
  isSelected: boolean;
  isIsolated: boolean;
  isFlagged: boolean;
  onSelect: (def: CorrectiveDefinition) => void;
  onToggleIsolate: (key: string) => void;
  onToggleFlag: (key: string) => void;
}) {
  const dk = defKey(def);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        borderLeft: isIsolated
          ? "2px solid #0ff"
          : isSelected
            ? "2px solid #ff0"
            : "2px solid transparent",
        background: isIsolated
          ? "rgba(0,255,255,0.06)"
          : isSelected
            ? "rgba(255,255,0,0.08)"
            : "transparent",
      }}
    >
      {/* Flag toggle */}
      <button
        data-no-drag
        onClick={(e) => {
          e.stopPropagation();
          onToggleFlag(dk);
        }}
        title={isFlagged ? "Unflag corrective" : "Flag as problematic"}
        style={{
          width: 16,
          flexShrink: 0,
          border: "none",
          background: "transparent",
          color: isFlagged ? "#e88" : "rgba(255,255,255,0.1)",
          fontFamily: "monospace",
          fontSize: 10,
          cursor: "pointer",
          padding: "2px 0 2px 2px",
          textAlign: "center",
          lineHeight: 1,
        }}
      >
        {isFlagged ? "\u25CF" : "\u25CB"}
      </button>

      {/* Isolate toggle */}
      <button
        data-no-drag
        onClick={(e) => {
          e.stopPropagation();
          onToggleIsolate(dk);
        }}
        title={isIsolated ? "Stop isolating" : "Isolate — solo this corrective"}
        style={{
          width: 16,
          flexShrink: 0,
          border: "none",
          background: "transparent",
          color: isIsolated ? "#0ff" : "rgba(255,255,255,0.1)",
          fontFamily: "monospace",
          fontSize: 9,
          cursor: "pointer",
          padding: "2px 0 2px 0",
          textAlign: "center",
          lineHeight: 1,
        }}
      >
        {isIsolated ? "\u25C9" : "\u25CE"}
      </button>

      {/* Main row */}
      <button
        data-no-drag
        onClick={() => onSelect(def)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flex: 1,
          padding: "2px 0 2px 0",
          border: "none",
          background: "transparent",
          color: isFlagged ? "#e88" : isSelected ? "#ff0" : "#ccc",
          fontFamily: "monospace",
          fontSize: 10,
          cursor: "pointer",
          textAlign: "left",
          minWidth: 0,
        }}
        title={`${def.bone} — ${degStr(def.activationAngle)} activation`}
      >
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {def.action}
        </span>
        <span
          data-bound-key={dk}
          style={{
            flexShrink: 0,
            color: "#e88",
            fontSize: 8,
            opacity: 0.8,
          }}
        />
        <span
          data-def-key={dk}
          style={{
            width: 36,
            textAlign: "right",
            flexShrink: 0,
            color: COLOR_TEXT_FAINT,
          }}
        />
        <span
          style={{
            width: 30,
            textAlign: "right",
            flexShrink: 0,
            color: COLOR_TEXT_DIM,
            fontSize: 9,
          }}
        >
          {degStr(def.activationAngle)}
        </span>
      </button>
    </div>
  );
}
